import React, { useState } from "react";
import { X, Trash2, ShoppingBag, Loader2, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyShip = { name: "", email: "", address: "", city: "", state: "", zip: "", country: "" };

export default function CartDrawer({ open, items, onClose, onRemove }) {
  const [step, setStep] = useState("cart"); // cart | shipping
  const [ship, setShip] = useState(emptyShip);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const total = items.reduce((s, i) => s + i.qty * parseFloat(i.price.replace("$", "")), 0);
  const hasPhysical = items.some((i) => !i.digital);

  const startCheckout = async (shipping) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({ id: i.id, qty: i.qty, size: i.size || null })),
          origin_url: window.location.origin,
          shipping: shipping || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Could not start checkout");
      }
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const onCheckoutClick = () => {
    if (items.length === 0) return;
    if (hasPhysical) {
      setStep("shipping");
    } else {
      startCheckout(null);
    }
  };

  const submitShipping = (e) => {
    e.preventDefault();
    startCheckout(ship);
  };

  const field = (key, label, type = "text", required = true) => (
    <div className="flex flex-col gap-1">
      <label className="font-tech text-[10px] tracking-[0.2em] text-[#15100E]/60">{label}</label>
      <input
        data-testid={`ship-${key}`}
        type={type}
        required={required}
        value={ship[key]}
        onChange={(e) => setShip((s) => ({ ...s, [key]: e.target.value }))}
        className="bg-[#ECE7DB] border-2 border-[#15100E] px-3 py-2 font-body text-sm text-[#15100E] focus:outline-none focus:border-[#7A1623]"
      />
    </div>
  );

  return (
    <>
      <div
        className={`fixed inset-0 z-[96] transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        style={{ background: "rgba(21,16,14,0.6)" }}
        onClick={onClose}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[97] h-screen w-[88vw] max-w-[400px] bg-[#ECE7DB] text-[#15100E] border-l-2 border-[#15100E] flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between px-5 h-16 border-b-2 border-[#15100E] shrink-0">
          <h3 className="font-display font-extrabold text-2xl">{step === "shipping" ? "SHIPPING" : "YOUR CART"}</h3>
          <button data-testid="cart-close" onClick={onClose} className="h-9 w-9 bg-[#15100E] text-[#ECE7DB] flex items-center justify-center hover:bg-[#4E0E18] transition-colors">
            <X size={18} />
          </button>
        </div>

        {step === "cart" && (
          <>
            <div className="flex-1 overflow-y-auto bg-scroll p-5 space-y-3">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center opacity-60">
                  <ShoppingBag size={34} />
                  <p className="font-tech text-xs tracking-widest mt-3">CART IS EMPTY</p>
                </div>
              ) : (
                items.map((i) => (
                  <div key={i.lineId || i.id} data-testid={`cart-line-${i.lineId || i.id}`} className="flex items-center gap-3 border-2 border-[#15100E] p-2">
                    <img src={i.img} alt={i.name} className="h-14 w-14 object-cover film border border-[#15100E]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-sm leading-none truncate">{i.name}</p>
                      {i.size && <p className="font-tech text-[#15100E]/60 text-[10px] tracking-widest mt-1">SIZE {i.size}</p>}
                      <p className="font-tech text-[#7A1623] text-xs mt-1">{i.qty} × {i.price}</p>
                    </div>
                    <button data-testid={`cart-remove-${i.lineId || i.id}`} onClick={() => onRemove(i.lineId || i.id)} className="text-[#15100E]/60 hover:text-[#4E0E18] transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="border-t-2 border-[#15100E] p-5 shrink-0">
              <div className="flex items-center justify-between mb-4">
                <span className="font-tech text-xs tracking-widest">SUBTOTAL</span>
                <span data-testid="cart-total" className="font-display font-extrabold text-2xl">${total.toFixed(2)}</span>
              </div>
              {error && <p data-testid="checkout-error" className="font-tech text-[#7A1623] text-[11px] mb-3">{error}</p>}
              <button
                data-testid="cart-checkout"
                onClick={onCheckoutClick}
                disabled={items.length === 0 || loading}
                className="w-full font-tech tracking-[0.2em] text-sm text-[#ECE7DB] bg-[#15100E] py-3.5 flex items-center justify-center gap-2 hover:bg-[#4E0E18] transition-colors disabled:opacity-40"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ShoppingBag size={16} />}
                {loading ? "REDIRECTING…" : "CHECKOUT"}
              </button>
              <p className="font-tech text-center text-[#15100E]/40 text-[10px] mt-2 tracking-wide">SECURE STRIPE CHECKOUT</p>
            </div>
          </>
        )}

        {step === "shipping" && (
          <form onSubmit={submitShipping} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto bg-scroll p-5 space-y-3">
              <button type="button" onClick={() => setStep("cart")} className="flex items-center gap-1 font-tech text-[11px] tracking-[0.2em] text-[#15100E]/60 hover:text-[#7A1623] mb-1">
                <ArrowLeft size={13} /> BACK TO CART
              </button>
              {field("name", "FULL NAME")}
              {field("email", "EMAIL", "email")}
              {field("address", "STREET ADDRESS")}
              <div className="grid grid-cols-2 gap-3">
                {field("city", "CITY")}
                {field("state", "STATE / REGION")}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field("zip", "ZIP / POSTAL")}
                {field("country", "COUNTRY")}
              </div>
            </div>
            <div className="border-t-2 border-[#15100E] p-5 shrink-0">
              <div className="flex items-center justify-between mb-4">
                <span className="font-tech text-xs tracking-widest">TOTAL</span>
                <span className="font-display font-extrabold text-2xl">${total.toFixed(2)}</span>
              </div>
              {error && <p data-testid="checkout-error" className="font-tech text-[#7A1623] text-[11px] mb-3">{error}</p>}
              <button
                data-testid="shipping-continue"
                type="submit"
                disabled={loading}
                className="w-full font-tech tracking-[0.2em] text-sm text-[#ECE7DB] bg-[#15100E] py-3.5 flex items-center justify-center gap-2 hover:bg-[#4E0E18] transition-colors disabled:opacity-40"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ShoppingBag size={16} />}
                {loading ? "REDIRECTING…" : "CONTINUE TO PAYMENT"}
              </button>
            </div>
          </form>
        )}
      </aside>
    </>
  );
}
